import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { CallPlaybackContext } from './CallRecording';
import './transcriptChat.css';

const TIMESTAMP_SPLIT = /(?<![\d:])(?=\d{1,2}:\d{2}(?::\d{2})?:\s)/;
// "00:01 Igor: Hello?" (current format) or "00:03: Hello?" (older, no speaker)
const SEGMENT = /^(?:\[?(\d{1,2}:\d{2}(?::\d{2})?)\]?:?\s*)?(?:([A-Z][\w.'-]*(?: [A-Z0-9][\w.'-]*){0,3}):\s+)?([\s\S]*)$/;

const toSeconds = (t) => (t ? t.split(':').reduce((acc, p) => acc * 60 + Number(p), 0) : null);
const firstName = (name) => (name || '').trim().split(/\s+/)[0].toLowerCase();

function namesMatch(a, b) {
    const x = firstName(a), y = firstName(b);
    if (!x || !y) return false;
    return x === y || (Math.min(x.length, y.length) >= 3 && (x.startsWith(y) || y.startsWith(x)));
}

// The agent is the speaker matching the call's user; failing that, the
// second person to speak (the one being called usually answers first).
function findAgent(speakers, userName) {
    return speakers.find((s) => namesMatch(s, userName)) || speakers[1] || null;
}

export function parseTranscript(raw, userName) {
    const segments = String(raw || '')
        .split(/\r?\n/)
        .flatMap((line) => line.split(TIMESTAMP_SPLIT))
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => {
            const [, time, speaker, text] = s.match(SEGMENT);
            return { time: time || '', seconds: toSeconds(time), speaker: speaker || '', text: text.trim() };
        })
        .filter((s) => s.text)
        .reduce((merged, s) => {
            const last = merged[merged.length - 1];
            if (last && last.speaker === s.speaker && last.time === s.time) last.text += ` ${s.text}`;
            else merged.push({ ...s });
            return merged;
        }, []);

    const speakers = [...new Set(segments.map((s) => s.speaker).filter(Boolean))];
    if (!speakers.length) {
        return segments.map((s, i) => ({ ...s, side: i % 2 === 0 ? 'client' : 'you' }));
    }
    const agent = findAgent(speakers, userName);
    return segments.map((s) => ({ ...s, side: s.speaker && s.speaker === agent ? 'you' : 'client' }));
}

export default function TranscriptChat({ transcription, userName, autoScroll = true }) {
    const messages = useMemo(() => parseTranscript(transcription, userName), [transcription, userName]);
    const currentTime = useContext(CallPlaybackContext);
    const chatRef = useRef(null);
    const activeRef = useRef(null);

    // Latest line whose timestamp the playback has reached; none before play starts.
    const activeIndex = useMemo(() => {
        if (!currentTime) return -1;
        let index = -1;
        messages.forEach((m, i) => { if (m.seconds != null && m.seconds <= currentTime) index = i; });
        return index;
    }, [messages, currentTime]);

    // Scroll only the chat box (not the page) to keep the active line centred.
    useEffect(() => {
        const chat = chatRef.current;
        const el = activeRef.current;
        if (!autoScroll || !chat || !el) return;
        chat.scrollTo({ top: el.offsetTop - chat.clientHeight / 2 + el.offsetHeight / 2, behavior: 'smooth' });
    }, [activeIndex, autoScroll]);

    if (!messages.length) return null;

    return (
        <div className="tc-chat" ref={chatRef}>
            {messages.map((m, i) => (
                <div
                    key={i}
                    ref={i === activeIndex ? activeRef : undefined}
                    className={`tc-row tc-${m.side}${i === activeIndex ? ' tc-active' : ''}`}
                >
                    {m.speaker && (i === 0 || messages[i - 1].speaker !== m.speaker) && (
                        <div className="tc-label">{m.speaker}</div>
                    )}
                    <div className="tc-bubble">{m.text}</div>
                    {m.time && <div className="tc-time">{m.time}</div>}
                </div>
            ))}
        </div>
    );
}
